import React, { forwardRef } from 'react';
import { Button, ButtonProps, useButtonGroup } from '@chakra-ui/react';

/**
 * 読み取り専用（<fieldset disabled>）の影響を受けないボタン
 *
 * 承認完了等で画面全体を fieldset で無効化していても、GoogleSpreadを開く（値をコピーする）操作は
 * 許可したいため、button要素ではなく div要素として描画する（fieldset disabled は div に作用しない）。
 * そのため isDisabled / isLoading 時のクリック抑止とキーボード操作は本コンポーネントで制御する。
 * ※ButtonGroup isAttached で使う場合、隣接ボタンも本コンポーネントに揃えること（要素種別が異なると角丸が崩れる）
 * ※Chakra UI v2（as / isDisabled / isLoading）前提。メジャー更新時は動作を再検証すること
 */
export const ReadOnlyExemptButton = forwardRef<HTMLDivElement, ButtonProps>((props, ref) => {
  const { onClick, onKeyDown, isDisabled, isLoading, style, ...rest } = props;
  // ButtonGroup の isDisabled も考慮する（Chakra内部の見た目の無効化と合わせる）
  const group = useButtonGroup();
  const isInactive = !!isDisabled || !!group?.isDisabled || !!isLoading;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isInactive) {
      return;
    }
    onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement>);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e as unknown as React.KeyboardEvent<HTMLButtonElement>);
    // キー押しっぱなしによる連続実行を防ぐ
    if (isInactive || e.repeat) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.currentTarget.click();
    }
  };

  return (
    <Button
      {...rest}
      ref={ref as unknown as React.Ref<HTMLButtonElement>}
      as="div"
      role="button"
      tabIndex={isInactive ? -1 : 0}
      aria-disabled={isInactive}
      aria-busy={!!isLoading}
      isDisabled={isDisabled}
      isLoading={isLoading}
      onClick={handleClick as unknown as React.MouseEventHandler<HTMLButtonElement>}
      onKeyDown={handleKeyDown as unknown as React.KeyboardEventHandler<HTMLButtonElement>}
      // 親の fieldset に pointerEvents:'none' が指定されていてもクリックできるようにする
      style={{ pointerEvents: 'auto', ...style }}
    />
  );
});

ReadOnlyExemptButton.displayName = 'ReadOnlyExemptButton';

export default ReadOnlyExemptButton;
